/**
 * Global search bar — keyword router with type-ahead suggestions.
 *
 * UX:
 *  - User types → suggestions appear (matched entity types + jump targets).
 *  - Arrow keys + Enter to select; Esc closes.
 *  - Cmd/Ctrl+K focuses the input from anywhere on the page.
 *
 * Routing logic is purely client-side (no backend round-trip) — the
 * keyword map below covers the demo surface.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search, ArrowRight, Hash } from "lucide-react";

// ── Keyword → route map ────────────────────────────────────────────
// Each entry: { match: RegExp | (term: string) => boolean, label, hint, path, queryKey? }
const ROUTES = [
  // Direct ID matches (highest priority)
  { test: (t) => /^agt-/i.test(t),     label: "Agent Inventory",       hint: "Agent ID lookup",       path: "/govern/agents",        kind: "agent" },
  { test: (t) => /^shadow-/i.test(t),  label: "Shadow AI",             hint: "Unsanctioned tool",     path: "/govern/agents",        kind: "agent" },
  { test: (t) => /^pol-/i.test(t),     label: "Policy Catalogue",      hint: "Policy ID lookup",      path: "/govern/policies",      kind: "policy" },
  { test: (t) => /^inc-/i.test(t),     label: "Incident Register",     hint: "Incident ID lookup",    path: "/govern/incidents",     kind: "incident" },
  { test: (t) => /^opp-/i.test(t),     label: "Opportunity Radar",     hint: "Opportunity ID lookup", path: "/identify/opportunities", kind: "opportunity" },
  { test: (t) => /^dr-/i.test(t),      label: "Deployment Requests",   hint: "Request ID lookup",     path: "/deploy/requests",      kind: "request" },
  { test: (t) => /^rpt-/i.test(t),     label: "Report Library",        hint: "Report ID lookup",      path: "/reports/library",      kind: "report" },
  { test: (t) => /^aud-/i.test(t),     label: "Audit Log",             hint: "Audit ID lookup",       path: "/settings/audit",       kind: "audit" },

  // Keyword matches
  { keywords: ["agent", "agents", "inventory", "governed", "shadow"],  label: "Agent Inventory",     hint: "All governed + shadow AI",  path: "/govern/agents",        kind: "agent" },
  { keywords: ["aml", "ctf", "kyc", "sanctions", "alert triage"],       label: "Agent Inventory",     hint: "AML Alert Triage Agent + financial-crime fleet", path: "/govern/agents",  kind: "agent" },
  { keywords: ["fraud", "case summarisation", "case summarization"],    label: "Agent Inventory",     hint: "Fraud Case Summarisation Agent",  path: "/govern/agents",        kind: "agent" },
  { keywords: ["credit", "underwriting", "loan", "lending"],            label: "Agent Inventory",     hint: "Credit Risk Assessment Assistant",path: "/govern/agents",        kind: "agent" },
  { keywords: ["mortgage", "document extraction"],                       label: "Agent Inventory",     hint: "Mortgage Document Extraction Agent", path: "/govern/agents",     kind: "agent" },
  { keywords: ["payments", "anomaly"],                                   label: "Agent Inventory",     hint: "Payments Anomaly Detection Agent",path: "/govern/agents",        kind: "agent" },
  { keywords: ["contact centre", "contact center", "copilot"],          label: "Agent Inventory",     hint: "Contact Centre Copilot",   path: "/govern/agents",        kind: "agent" },
  { keywords: ["vulnerable", "hardship", "customer detection"],         label: "Agent Inventory",     hint: "Vulnerable Customer Detection Agent", path: "/govern/agents",   kind: "agent" },
  { keywords: ["claims", "claim"],                                       label: "Agent Inventory",     hint: "Claims-side agents (insurance tenant)", path: "/govern/agents", kind: "agent" },
  { keywords: ["underwrite", "policy quote", "premium"],                 label: "Agent Inventory",     hint: "Underwriting agents (insurance tenant)", path: "/govern/agents", kind: "agent" },
  { keywords: ["policy", "policies", "rule", "rules"],                  label: "Policy Catalogue",    hint: "Published rules + drafts",  path: "/govern/policies",      kind: "policy" },
  { keywords: ["incident", "incidents", "outage", "breach"],            label: "Incident Register",   hint: "Open + resolved incidents", path: "/govern/incidents",     kind: "incident" },
  { keywords: ["oversight", "hil", "human", "approval", "queue"],       label: "Oversight Console",   hint: "Human-in-the-loop queue",   path: "/govern/oversight",     kind: "oversight" },
  { keywords: ["audit", "chain", "log", "evidence"],                    label: "Audit Log",           hint: "Hash-chained trail",        path: "/settings/audit",       kind: "audit" },
  { keywords: ["compliance", "apra", "nist", "iso", "framework", "234", "230", "42001"], label: "Compliance Centre", hint: "Framework coverage", path: "/compliance",        kind: "compliance" },
  { keywords: ["report", "reports", "pack", "library", "scheduled"],    label: "Reports",             hint: "Packs · builder · library", path: "/reports",              kind: "report" },
  { keywords: ["vendor", "vendors", "integration", "openai", "anthropic", "vertex", "bedrock", "azure", "copilot", "mistral", "cohere"], label: "Vendor Integration Hub", hint: "Connected + catalogue", path: "/vendors/integrations", kind: "vendor" },
  { keywords: ["performance", "drift", "model"],                        label: "Model Performance & Drift", hint: "Drift signals",         path: "/measure/performance",  kind: "measure" },
  { keywords: ["cost", "spend", "optimisation", "optimization", "burn"], label: "Cost Optimisation",   hint: "Spend, ROI, optimisers",    path: "/measure/cost",         kind: "measure" },
  { keywords: ["benchmark", "benchmarking", "peer"],                    label: "Benchmarking",        hint: "Peer percentile bands",     path: "/measure/benchmark",    kind: "measure" },
  { keywords: ["user", "users", "role", "roles", "rbac"],               label: "Users & Roles",       hint: "RBAC + personas",           path: "/settings/users",       kind: "settings" },
  { keywords: ["opportunity", "opportunities", "radar", "idea"],        label: "Opportunity Radar",   hint: "Pipeline of AI ideas",      path: "/identify/opportunities", kind: "opportunity" },
  { keywords: ["deploy", "deployment", "request", "workflow"],          label: "Deployment Requests", hint: "Stage workflow",            path: "/deploy/requests",      kind: "request" },
  { keywords: ["dashboard", "home", "ceo", "caio"],                     label: "Home dashboard",      hint: "Executive overview",        path: "/dashboard",            kind: "home" },
];

const HIGH_RISK_KEYWORDS = ["high risk", "high-risk", "risk", "drift", "alert"];

function getSuggestions(query) {
  const q = query.trim().toLowerCase();
  if (!q) return [];
  const matched = ROUTES.filter((r) => {
    if (r.test) return r.test(q);
    if (r.keywords) return r.keywords.some((k) => q.includes(k));
    return false;
  });
  // De-dupe by path (some keywords overlap)
  const seen = new Set();
  const unique = matched.filter((m) => {
    if (seen.has(m.path)) return false;
    seen.add(m.path);
    return true;
  });
  // Smart hint: "high risk" routes to Agent Inventory + applies a hint
  const wantsHighRisk = HIGH_RISK_KEYWORDS.some((k) => q.includes(k));
  if (wantsHighRisk && !unique.some((u) => u.path === "/govern/agents")) {
    unique.unshift({ label: "Agent Inventory", hint: "High-risk agents", path: "/govern/agents", kind: "agent" });
  }
  return unique.slice(0, 6);
}

export default function GlobalSearch() {
  const navigate = useNavigate();
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const [cursor, setCursor] = useState(0);
  const inputRef = useRef(null);
  const containerRef = useRef(null);

  const suggestions = useMemo(() => getSuggestions(query), [query]);

  // Cmd/Ctrl+K → focus input from anywhere
  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        inputRef.current?.focus();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  // Close on outside click
  useEffect(() => {
    const onClick = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    window.addEventListener("mousedown", onClick);
    return () => window.removeEventListener("mousedown", onClick);
  }, []);

  const choose = useCallback(
    (s) => {
      if (!s) return;
      navigate(s.path);
      setOpen(false);
      setQuery("");
      inputRef.current?.blur();
    },
    [navigate],
  );

  const onKeyDown = useCallback(
    (e) => {
      if (e.key === "Escape") {
        setOpen(false);
        inputRef.current?.blur();
        return;
      }
      if (!suggestions.length) return;
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setCursor((c) => Math.min(c + 1, suggestions.length - 1));
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        setCursor((c) => Math.max(c - 1, 0));
      } else if (e.key === "Enter") {
        e.preventDefault();
        choose(suggestions[cursor]);
      }
    },
    [suggestions, cursor, choose],
  );

  return (
    <div ref={containerRef} className="flex-1 max-w-[460px] relative">
      <Search
        size={15}
        className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-secondary pointer-events-none"
      />
      <input
        ref={inputRef}
        type="search"
        value={query}
        onChange={(e) => { setQuery(e.target.value); setOpen(true); setCursor(0); }}
        onFocus={() => setOpen(true)}
        onKeyDown={onKeyDown}
        placeholder="Search agents, policies, incidents…"
        className="w-full h-9 pl-9 pr-12 bg-inset border border-transparent rounded-md text-sm placeholder:text-ink-disabled focus:bg-raised focus:border-tralenex-300 focus:ring-2 focus:ring-tralenex-200 outline-none transition-all"
        data-testid="header-search-input"
        autoComplete="off"
      />
      <kbd className="absolute right-2 top-1/2 -translate-y-1/2 hidden sm:inline-flex items-center gap-0.5 px-1.5 h-5 rounded border border-border-subtle bg-raised text-[10px] font-mono text-ink-secondary">
        ⌘K
      </kbd>

      {open && query.trim() && (
        <div
          className="absolute left-0 right-0 top-full mt-2 rounded-md border border-border-subtle bg-raised shadow-e3 anim-fade-up overflow-hidden z-50"
          data-testid="header-search-suggestions"
        >
          {suggestions.length === 0 ? (
            <div className="px-4 py-6 text-center text-meta text-ink-secondary">
              No matches for <span className="font-mono text-ink-primary">&ldquo;{query}&rdquo;</span>.<br />
              Try <span className="font-mono">agents</span>, <span className="font-mono">policies</span>, <span className="font-mono">APRA</span>, <span className="font-mono">cost</span>…
            </div>
          ) : (
            <ul className="py-1">
              {suggestions.map((s, i) => (
                <li key={s.path}>
                  <button
                    type="button"
                    onClick={() => choose(s)}
                    onMouseEnter={() => setCursor(i)}
                    className={`w-full flex items-center gap-3 px-3 py-2 text-left transition-colors ${
                      i === cursor ? "bg-tralenex-50" : "hover:bg-inset"
                    }`}
                    data-testid={`header-search-suggestion-${s.kind}`}
                  >
                    <Hash size={12} className="text-ink-disabled shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-ink-primary truncate">{s.label}</p>
                      <p className="text-meta text-ink-secondary truncate">{s.hint}</p>
                    </div>
                    <ArrowRight size={12} className="text-ink-disabled shrink-0" />
                  </button>
                </li>
              ))}
            </ul>
          )}
          <div className="px-3 py-2 border-t border-border-subtle bg-inset/40 flex items-center justify-between text-[10px] text-ink-secondary">
            <span><kbd className="font-mono">↑↓</kbd> navigate · <kbd className="font-mono">↵</kbd> open · <kbd className="font-mono">esc</kbd> close</span>
            <span>{suggestions.length} match{suggestions.length === 1 ? "" : "es"}</span>
          </div>
        </div>
      )}
    </div>
  );
}
